import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

export default class extends Controller {
  static targets = ["chart", "trend"]
  static values = { chartsDataUrl: String }

  connect() {
    this.onDashboardCharts = (event) => {
      const data = event.detail
      if (data?.stacked_data && data?.stacked_labels) {
        this.createMiniCharts(data.stacked_data, data.stacked_labels)
      }
    }
    window.addEventListener('admin:dashboard-charts', this.onDashboardCharts)

    // Fallback when used without dashboard-charts (e.g. isolated page).
    if (this.hasChartsDataUrlValue && this.chartsDataUrlValue) {
      this.loadChartData()
    }
  }

  disconnect() {
    window.removeEventListener('admin:dashboard-charts', this.onDashboardCharts)
  }

  async loadChartData() {
    try {
      const response = await fetch(this.chartsDataUrlValue)
      const data = await response.json()
      
      this.createMiniCharts(data.stacked_data, data.stacked_labels)
    } catch (error) {
      console.error('Error loading mini chart data:', error)
    }
  }

  createMiniCharts(stackedData, stackedLabels) {
    this.chartTargets.forEach(canvas => {
      const metric = canvas.dataset.metric
      this.createMiniChart(canvas, metric, stackedData, stackedLabels)
    })
    
    // Update trend indicators
    this.updateTrendIndicators(stackedData, stackedLabels)
  }

  createMiniChart(canvas, metric, stackedData, stackedLabels) {
    const ctx = canvas.getContext('2d')
    const tenants = Object.keys(stackedData)
    
    // Get last 4 weeks of data for mini chart
    const last4WeeksLabels = stackedLabels.slice(-4)
    
    // Prepare data for the specific metric
    const data = last4WeeksLabels.map(date => {
      return tenants.reduce((sum, tenant) => {
        return sum + (stackedData[tenant][date]?.[metric] || 0)
      }, 0)
    })

    // Get color based on metric
    const color = this.getMetricColor(metric)
    
    new Chart(ctx, {
      type: 'line',
      data: {
        labels: last4WeeksLabels.map(label => {
          // Extract just the week part for mini chart
          return label.split(' - ')[0].split('.').slice(1).join('.')
        }),
        datasets: [{
          data: data,
          borderColor: color,
          backgroundColor: 'transparent', // No background fill
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        },
        scales: {
          x: {
            display: false
          },
          y: {
            display: false
          }
        },
        interaction: {
          intersect: false
        }
      }
    })
  }

  updateTrendIndicators(stackedData, stackedLabels) {
    const tenants = Object.keys(stackedData)
    
    this.trendTargets.forEach(trendElement => {
      const metric = trendElement.dataset.metric
      
      // Get last 4 weeks of data for trend calculation
      const last4WeeksLabels = stackedLabels.slice(-4)
      const data = last4WeeksLabels.map(date => {
        return tenants.reduce((sum, tenant) => {
          return sum + (stackedData[tenant][date]?.[metric] || 0)
        }, 0)
      })
      
      // Calculate trend
      const trend = this.calculateTrend(data)
      
      // Set trend indicator
      trendElement.innerHTML = this.getTrendIcon(trend)
    })
  }

  calculateTrend(data) {
    if (data.length < 2) return 0
    
    // Simple linear regression to calculate trend
    const n = data.length
    const xSum = Array.from({length: n}, (_, i) => i).reduce((sum, x) => sum + x, 0)
    const ySum = data.reduce((sum, y) => sum + y, 0)
    const xySum = data.reduce((sum, y, x) => sum + (x * y), 0)
    const xSquaredSum = Array.from({length: n}, (_, i) => i * i).reduce((sum, x) => sum + x, 0)
    
    const slope = (n * xySum - xSum * ySum) / (n * xSquaredSum - xSum * xSum)
    
    // Determine trend direction based on slope
    if (slope > 0.1) return 1  // Upward trend
    if (slope < -0.1) return -1  // Downward trend
    return 0  // Stable trend
  }

  getTrendIcon(trend) {
    switch(trend) {
      case 1:
        return '<i class="bi bi-arrow-up text-success" style="font-size: 1.2rem;"></i>'
      case -1:
        return '<i class="bi bi-arrow-down text-danger" style="font-size: 1.2rem;"></i>'
      default:
        return '<i class="bi bi-dash text-dark" style="font-size: 1.2rem;"></i>'
    }
  }

  getMetricColor(metric) {
    const colors = {
      tenants: 'rgba(13, 110, 253, 1)',      // Bootstrap primary
      properties: 'rgba(25, 135, 84, 1)',    // Bootstrap success
      files: 'rgba(13, 202, 240, 1)',        // Bootstrap info
      filesSize: 'rgba(255, 193, 7, 1)',     // Bootstrap warning
      users: 'rgba(108, 117, 125, 1)'        // Bootstrap secondary
    }
    return colors[metric] || 'rgba(108, 117, 125, 1)'
  }
}
